// src/pages/player/PlayerProfile.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../api/api';
import { AuthContext } from '../../context/AuthContext';
import { resolveAssetUrl } from '../../api/config';
import { FaEdit, FaSpinner, FaCamera, FaEye, FaKey } from 'react-icons/fa';
import DisabilitySelect from '../../components/common/DisabilitySelect';
import FileUploadWithScanner from '../../components/common/FileUploadWithScanner';
import DocumentScannerModal from '../../components/common/DocumentScannerModal';

const TabButton = ({ isActive, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-6 py-3 font-semibold rounded-t-lg transition-colors ${
      isActive
        ? 'bg-white text-indigo-900 border-b-2 border-indigo-900'
        : 'bg-transparent text-slate-500 hover:bg-slate-200'
    }`}
  >
    {children}
  </button>
);

const ViewField = ({ label, value }) => (
  <div>
    <p className="text-sm font-medium text-slate-500">{label}</p>
    <p className="text-lg text-slate-800">{value || '-'}</p>
  </div>
);

const EditInput = ({ label, name, value, onChange, error, type = 'text', ...props }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <input
      id={name}
      name={name}
      type={type}
      value={value || ''}
      onChange={onChange}
      className={`w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none ${error ? 'border-red-500' : ''}`}
      {...props}
    />
  </div>
);

const DocumentPreview = ({ fileKey, url, onUpload, onClear, label, isEditing, currentFile }) => (
    <div className="bg-slate-100 p-4 rounded-lg">
        <p className="font-semibold text-slate-700 mb-2">{label}</p>
        {isEditing ? (
            <FileUploadWithScanner
                name={fileKey}
                accept=".pdf,image/*"
                value={currentFile || url}
                onChange={(file) => {
                    if (file) {
                        onUpload({ target: { name: fileKey, files: [file], value: file } });
                    } else {
                        onClear(fileKey);
                    }
                }}
                scannerTitle={`Scan ${label}`}
                targetFileName={`${fileKey}_document.jpg`}
            />
        ) : url ? (
            <div className="flex items-center justify-between">
                <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-indigo-900 hover:underline font-semibold text-sm">
                    <FaEye /> View Document
                </a>
            </div>
        ) : (
            <p className="text-sm text-slate-500">Not uploaded</p>
        )}
    </div>
);


const PlayerProfile = () => {
  const { setUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [initialProfile, setInitialProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [classifications, setClassifications] = useState([]);
  const [isPhotoScannerOpen, setIsPhotoScannerOpen] = useState(false);

  useEffect(() => {
    API.fetchMyClassifications()
      .then((res) => {
        setClassifications(res?.data || res || []);
      })
      .catch((err) => {
        console.error("Failed to fetch classifications:", err);
      });
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await API.fetchPlayerProfile();
        setProfile(res.data);
        setInitialProfile(res.data);
      } catch (err) {
        console.error("Failed to load profile", err);
        if (err.response?.status === 401) navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [navigate]);
  
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
        setProfile(p => ({ ...p, [name]: files[0] }));
    } else {
        setProfile(p => ({ ...p, [name]: value }));
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
        const formData = new FormData();
        // Only append changed values and files
        Object.keys(profile).forEach(key => {
            if (key === 'disability_types') {
                 formData.append('disability_types', JSON.stringify(Array.isArray(profile[key]) ? profile[key] : []));
            } else if (profile[key] !== initialProfile[key] || profile[key] instanceof File) {
                 formData.append(key, profile[key]);
            }
        });
        
        // As _method is required by laravel for PUT request with FormData
        formData.append('_method', 'PUT');

        const updatedProfile = await API.updateProfile(formData);
        setUser(updatedProfile.data); // Update context
        setProfile(updatedProfile.data);
        setInitialProfile(updatedProfile.data);
        setIsEditing(false);
    } catch (error) {
        console.error("Update failed", error);
    } finally {
        setLoading(false);
    }
  };

  if (loading && !profile) {
    return <div className="text-center p-12">Loading Profile...</div>;
  }
  
  if (!profile) {
    return <div className="text-center p-12 text-red-500">Could not load profile data.</div>;
  }

  const getFilePreviewUrl = (file) => {
    if (file instanceof File) return URL.createObjectURL(file);
    return resolveAssetUrl(file, { folder: "players", entityId: profile.id });
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">My Profile</h1>
          <p className="text-slate-600 text-lg">View and manage your personal details.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/change-password")}
            className="flex items-center gap-2 font-semibold text-slate-700 bg-slate-200 rounded-lg px-5 py-2 hover:bg-slate-300 transition-all"
          >
            <FaKey /> Change Password
          </button>
          {!isEditing ? (
            <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 font-semibold text-white bg-indigo-900 rounded-lg px-5 py-2 hover:bg-indigo-950 shadow-sm hover:shadow-md transition-all">
              <FaEdit /> Edit Profile
            </button>
          ) : (
            <div className="flex gap-3">
              <button onClick={handleUpdate} disabled={loading} className="flex items-center gap-2 font-semibold text-white bg-green-600 rounded-lg px-5 py-2 hover:bg-green-700 shadow-sm transition-all disabled:bg-green-400">
                 {loading ? <FaSpinner className="animate-spin" /> : '✓'} Save Changes
              </button>
              <button onClick={() => { setIsEditing(false); setProfile(initialProfile); }} className="font-semibold text-slate-700 bg-slate-200 rounded-lg px-5 py-2 hover:bg-slate-300 transition-all">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md">
        {/* Profile Header */}
        <div className="p-6 border-b border-slate-200 flex flex-col sm:flex-row items-center gap-6">
            <div className="relative">
                <img src={getFilePreviewUrl(profile.photo)} alt="Profile" className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"/>
                {isEditing && (
                    <button
                        type="button"
                        onClick={() => setIsPhotoScannerOpen(true)}
                        className="absolute bottom-1 right-1 w-9 h-9 bg-indigo-900 text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-indigo-950 shadow-md transition"
                        title="Scan / Update Photo"
                    >
                        <FaCamera />
                    </button>
                )}
            </div>
            <div>
                 <h2 className="text-3xl font-bold text-slate-800">{profile.name}</h2>
                 <p className="text-slate-500">{profile.email}</p>
            </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 px-6">
          <nav className="flex gap-6">
            <TabButton isActive={activeTab === 'personal'} onClick={() => setActiveTab('personal')}>Personal Details</TabButton>
            <TabButton isActive={activeTab === 'contact'} onClick={() => setActiveTab('contact')}>Contact</TabButton>
            <TabButton isActive={activeTab === 'medical'} onClick={() => setActiveTab('medical')}>Health & Room Details</TabButton>
            <TabButton isActive={activeTab === 'documents'} onClick={() => setActiveTab('documents')}>Documents</TabButton>
            <TabButton isActive={activeTab === 'classifications'} onClick={() => setActiveTab('classifications')}>Classifications</TabButton>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-8">
          {activeTab === 'personal' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {isEditing ? <EditInput label="Full Name" name="name" value={profile.name} onChange={handleChange} /> : <ViewField label="Full Name" value={profile.name} />}
                {isEditing ? <EditInput label="Father/Husband Name" name="relation" value={profile.relation} onChange={handleChange} /> : <ViewField label="Father/Husband Name" value={profile.relation} />}
                {isEditing ? <EditInput label="Date of Birth" name="dob" type="date" value={profile.dob?.slice(0, 10)} onChange={handleChange} /> : <ViewField label="Date of Birth" value={profile.dob} />}
                {isEditing ? (
                     <div>
                        <label htmlFor="gender" className="block text-sm font-medium text-slate-700 mb-1">Gender</label>
                        <select id="gender" name="gender" value={profile.gender} onChange={handleChange} className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg">
                           <option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
                        </select>
                     </div>
                ) : <ViewField label="Gender" value={profile.gender} />}
                <ViewField label="Religion" value={profile.religion || '-'} />
                <ViewField label="Caste / Category" value={profile.caste || '-'} />
                <ViewField label="Subcaste" value={profile.subcaste || '-'} />
                <ViewField label="Registration ID" value={profile.registration_id || '-'} />
                <ViewField label="Aadhaar Number" value={profile.aadhaar_number} />
                <ViewField label="Email" value={profile.email} />
                <div className="col-span-1 md:col-span-2 lg:col-span-3">
                  {isEditing ? (
                    <DisabilitySelect
                      selected={profile.disability_types}
                      onChange={(selected) => setProfile((p) => ({ ...p, disability_types: selected }))}
                      required={true}
                    />
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Disability Type(s)</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Array.isArray(profile.disability_types) && profile.disability_types.length > 0 ? (
                          profile.disability_types.map((d, idx) => (
                            <span key={idx} className="px-2.5 py-1 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-lg text-sm font-medium">
                              {d}
                            </span>
                          ))
                        ) : (
                          <p className="text-lg text-slate-800">{profile.disability_types || '-'}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
            </div>
          )}

          {activeTab === 'contact' && (
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {isEditing ? <EditInput label="Mobile Number" name="mobile" value={profile.mobile} onChange={handleChange} /> : <ViewField label="Mobile Number" value={profile.mobile} />}
                {isEditing ? <EditInput label="Pincode" name="pincode" value={profile.pincode} onChange={handleChange} /> : <ViewField label="Pincode" value={profile.pincode} />}
                {isEditing ? <EditInput label="District" name="district" value={profile.district} onChange={handleChange} /> : <ViewField label="District" value={profile.district} />}
                {isEditing ? <EditInput label="Mandal" name="mandal" value={profile.mandal} onChange={handleChange} /> : <ViewField label="Mandal" value={profile.mandal} />}
                {isEditing ? <EditInput label="State" name="state" value={profile.state} onChange={handleChange} /> : <ViewField label="State" value={profile.state} />}
                {isEditing ? <EditInput label="Address" name="address" value={profile.address} onChange={handleChange} className="md:col-span-2"/> : <ViewField label="Address" value={profile.address} />}
             </div>
          )}

          {activeTab === 'medical' && (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {isEditing ? (
                   <EditInput label="Allotted Room No" name="room_no" value={profile.room_no || ''} onChange={handleChange} placeholder="e.g. Room 101" />
                ) : (
                   <ViewField label="Allotted Room No" value={profile.room_no ? `Room ${profile.room_no}` : 'Not allotted yet'} />
                )}
                {isEditing ? (
                   <EditInput label="Medicine Timings" name="medicine_timings" value={profile.medicine_timings || ''} onChange={handleChange} placeholder="e.g. Morning 8 AM, Night 9 PM" />
                ) : (
                   <ViewField label="Medicine Timings" value={profile.medicine_timings || 'None'} />
                )}
                <div className="col-span-1 md:col-span-2">
                   {isEditing ? (
                      <div>
                         <label className="block text-sm font-medium text-slate-700 mb-1">Health Issues / Conditions</label>
                         <textarea name="health_issues" rows={2} value={profile.health_issues || ''} onChange={handleChange} placeholder="e.g. Mild Asthma, Allergy to dust" className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm" />
                      </div>
                   ) : (
                      <ViewField label="Health Issues / Conditions" value={profile.health_issues || 'None reported'} />
                   )}
                </div>
                <div className="col-span-1 md:col-span-2">
                   {isEditing ? (
                      <div>
                         <label className="block text-sm font-medium text-slate-700 mb-1">Prescribed Medicines</label>
                         <textarea name="medicines" rows={2} value={profile.medicines || ''} onChange={handleChange} placeholder="e.g. Paracetamol 500mg, Asthalin Inhaler" className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm" />
                      </div>
                   ) : (
                      <ViewField label="Prescribed Medicines" value={profile.medicines || 'No regular medicines prescribed'} />
                   )}
                </div>
             </div>
          )}

          {activeTab === 'documents' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <DocumentPreview label="Aadhaar Card" fileKey="aadhaar" url={getFilePreviewUrl(profile.aadhaar)} currentFile={profile.aadhaar instanceof File ? profile.aadhaar : null} onUpload={handleChange} onClear={() => setProfile({...profile, aadhaar: null})} isEditing={isEditing} />
                <DocumentPreview label="Birth Certificate" fileKey="birth_certificate" url={getFilePreviewUrl(profile.birth_certificate)} currentFile={profile.birth_certificate instanceof File ? profile.birth_certificate : null} onUpload={handleChange} onClear={() => setProfile({...profile, birth_certificate: null})} isEditing={isEditing} />
                <DocumentPreview label="SADAREM Certificate" fileKey="sadarem" url={getFilePreviewUrl(profile.sadarem)} currentFile={profile.sadarem instanceof File ? profile.sadarem : null} onUpload={handleChange} onClear={() => setProfile({...profile, sadarem: null})} isEditing={isEditing} />
                <DocumentPreview label="Other Document" fileKey="others" url={getFilePreviewUrl(profile.others)} currentFile={profile.others instanceof File ? profile.others : null} onUpload={handleChange} onClear={() => setProfile({...profile, others: null})} isEditing={isEditing} />
              </div>
          )}

          {activeTab === 'classifications' && (
            <div>
              {classifications.length === 0 ? (
                <div className="text-center py-8 text-slate-500 font-medium">
                  No sports classifications recorded yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Sport Name</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Classification Code</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Discipline</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {classifications.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/55 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-800">{item.sport_name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 border border-indigo-200 text-indigo-700 uppercase">
                              {item.classification_code}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 capitalize">{item.discipline || "General"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <DocumentScannerModal
        isOpen={isPhotoScannerOpen}
        onClose={() => setIsPhotoScannerOpen(false)}
        onComplete={(file) => {
          setProfile((p) => ({ ...p, photo: file }));
        }}
        title="Scan / Update Profile Photo"
        targetFileName="profile_photo.jpg"
      />
    </div>
  );
};

export default PlayerProfile;
